import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { StickyNote, CheckCircle, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { useUnreadNotes } from '../contexts/UnreadNotesContext';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

function UserNotesSection({ userId, currentUser }) {
  const [userNotes, setUserNotes] = useState([]);
  const unreadNotesContext = useUnreadNotes();

  useEffect(() => {
    fetchUserNotes();
  }, []);

  const fetchUserNotes = async () => {
    try {
      const response = await axios.get(`${API}/user-notes`);
      setUserNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch user notes');
    }
  };

  const handleMarkNoteRead = async (noteId) => {
    try {
      await axios.post(`${API}/user-notes/${noteId}/mark-read`);
      toast.success('Note marked as read');
      fetchUserNotes();
      if (unreadNotesContext && unreadNotesContext.triggerRefresh) {
        unreadNotesContext.triggerRefresh(); // Update badge count
      }
    } catch (error) {
      toast.error('Failed to mark note as read');
    }
  };

  const canSeeReadReceipts = (note) => {
    // Note creator (admin) can see who read it
    return currentUser?.role === 'admin' && note.created_by === currentUser?.id;
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-yellow-500/20 rounded-lg">
          <StickyNote className="w-5 h-5 text-yellow-400" />
        </div>
        <h3 className="text-xl font-bold text-white">Messages from Admin</h3>
        {userNotes.filter(n => !n.is_read).length > 0 && (
          <span className="inline-flex items-center justify-center w-7 h-7 text-sm font-bold text-white bg-red-500 rounded-full">
            {userNotes.filter(n => !n.is_read).length}
          </span>
        )}
      </div>

      {userNotes.length > 0 ? (
        <div className="space-y-4">
          {userNotes.map((note) => (
            <div
              key={note.id}
              className={`p-4 rounded-lg border ${
                note.is_read
                  ? 'bg-slate-800/30 border-slate-700'
                  : 'bg-orange-500/10 border-orange-500/50'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <p className="text-white font-semibold mb-1">From: {note.created_by_name}</p>
                  <p className="text-slate-400 text-sm">
                    {new Date(note.created_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })} at {new Date(note.created_at).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto' })}
                  </p>
                </div>
                {!note.is_read && (
                  <Button
                    size="sm"
                    onClick={() => handleMarkNoteRead(note.id)}
                    className="bg-gradient-to-r from-green-500 to-green-600"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Read
                  </Button>
                )}
                {note.is_read && (
                  <span className="text-xs text-green-400 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Read
                  </span>
                )}
              </div>
              <p className="text-white whitespace-pre-wrap mb-3">{note.content}</p>
              
              {/* Read Receipts for Admin */}
              {canSeeReadReceipts(note) && note.read_receipts && note.read_receipts.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <div className="text-xs text-slate-400">
                    <div className="flex items-center gap-1 mb-1">
                      <Eye className="w-3 h-3" />
                      <span className="font-semibold">Read by:</span>
                    </div>
                    <div className="space-y-1 ml-4">
                      {note.read_receipts.map((receipt) => (
                        <div key={receipt.user_id}>
                          {receipt.user_name} <span className="text-slate-500">({formatRelativeTime(receipt.read_at)})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {note.is_read && note.read_at && !canSeeReadReceipts(note) && (
                <p className="text-slate-500 text-xs mt-2">
                  Read on: {new Date(note.read_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })} at {new Date(note.read_at).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto' })}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-slate-400 text-center py-8">No messages yet</p>
      )}
    </div>
  );
}

export default UserNotesSection;
